// ============================================================================
// LiveActivityTicker — real-time scrolling band showing recent verdicts from
// the platform. Drops on the landing right under the hero to give social
// proof: "this is alive, real people are using it right now".
// ============================================================================
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

const TONE = {
  TRADE:       { hex: '#10b981', label: 'TRADE' },
  AUTHORIZED:  { hex: '#10b981', label: 'TRADE' },
  NO_TRADE:    { hex: '#a855f7', label: 'NO-TRADE' },
  'NO-TRADE':  { hex: '#a855f7', label: 'NO-TRADE' },
  WAIT:        { hex: '#f59e0b', label: 'WAIT' },
  CONDITIONAL: { hex: '#f59e0b', label: 'WAIT' },
};

const fmtAge = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const fallbackRows = [
  { handle: 'jean-michel', asset: 'BTCUSD',  verdict: 'NO_TRADE', capital_saved: 420, created_at: new Date(Date.now() - 60000).toISOString() },
  { handle: 'alex',        asset: 'XAUUSD',  verdict: 'WAIT',     capital_saved: 0,   created_at: new Date(Date.now() - 5 * 60000).toISOString() },
  { handle: 'mike',        asset: 'NAS100',  verdict: 'TRADE',    capital_saved: 0,   created_at: new Date(Date.now() - 12 * 60000).toISOString() },
  { handle: 'sarah',       asset: 'ETHUSD',  verdict: 'NO_TRADE', capital_saved: 280, created_at: new Date(Date.now() - 24 * 60000).toISOString() },
  { handle: 'derek',       asset: 'BTCUSD',  verdict: 'WAIT',     capital_saved: 0,   created_at: new Date(Date.now() - 45 * 60000).toISOString() },
];

const LiveActivityTicker = () => {
  const [rows, setRows] = useState(fallbackRows);
  const [, forceRerender] = useState(0);

  useEffect(() => {
    let cancelled = false;
    supabase.rpc('get_public_recent_activity', { p_limit: 12 }).then(({ data, error }) => {
      if (cancelled || error) return;
      if (Array.isArray(data) && data.length > 0) setRows(data);
    }, () => {});
    // Re-tick every 30s so 'just now' / 'Xm ago' stays fresh
    const id = setInterval(() => forceRerender((n) => n + 1), 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Duplicate the list so the marquee loops seamlessly
  const loop = [...rows, ...rows];

  return (
    <div className="relative w-full overflow-hidden border-y border-white/5 bg-black/40 backdrop-blur-sm">
      <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-gray-900 to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-gray-900 to-transparent z-10 pointer-events-none" />
      <div className="flex items-center gap-2 py-3 animate-ticker">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-300 flex-shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          live activity
        </span>
        {loop.map((r, i) => {
          const tone = TONE[r.verdict] || TONE.WAIT;
          const label = tone.label;
          return (
            <div key={`${i}-${r.created_at}`}
              className="flex items-center gap-2 px-3 py-1 rounded-full border border-white/5 bg-white/[0.02] text-xs flex-shrink-0">
              <span className="text-gray-400 font-mono">@{r.handle}</span>
              <span className="text-gray-500">·</span>
              <span className="text-white font-mono font-semibold">${r.asset}</span>
              <span className="font-mono font-bold" style={{ color: tone.hex }}>→ {label}</span>
              {Number(r.capital_saved) > 0 && (
                <span className="text-emerald-300 text-[10px] font-mono">+${Number(r.capital_saved).toLocaleString()}</span>
              )}
              <span className="text-gray-600 text-[10px] font-mono">{fmtAge(r.created_at)}</span>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-ticker {
          width: max-content;
          animation: ticker 60s linear infinite;
        }
        .animate-ticker:hover { animation-play-state: paused; }
      `}</style>
    </div>
  );
};

export default LiveActivityTicker;
